/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
function _createMdxContent(props) {
  const _components = Object.assign({
    h2: "h2",
    p: "p",
    code: "code",
    h3: "h3",
    pre: "pre",
    a: "a"
  }, _provideComponents(), props.components);
  return _jsxs(_Fragment, {
    children: [_jsx(_components.h2, {
      children: "Existing CSS"
    }), "\n", _jsx(_components.p, {
      children: "There are a couple of implementation details that you should be aware of, if you choose to use\nstyled-components together with existing CSS."
    }), "\n", _jsxs(_components.p, {
      children: ["styled-components generates an actual stylesheet with classes, and attaches those classes to\nthe DOM nodes of styled components via the ", _jsx(_components.code, {
        children: "className"
      }), " prop.\nIt injects the generated stylesheet at the end of the head of the document during runtime."]
    }), "\n", _jsx(_components.h3, {
      children: "Styling normal React components"
    }), "\n", _jsxs(_components.p, {
      children: ["If you use the ", _jsx(_components.code, {
        children: "styled(MyComponent)"
      }), " notation and ", _jsx(_components.code, {
        children: "MyComponent"
      }), " does not\nrender the passed-in ", _jsx(_components.code, {
        children: "className"
      }), " prop, then no styles will be applied.\nTo avoid this issue, make sure your component attaches the passed-in ", _jsx(_components.code, {
        children: "className"
      }), " to a DOM node:"]
    }), "\n", _jsx(_components.pre, {
      children: _jsx(_components.code, {
        className: "language-tsx",
        children: "class MyComponent extends React.Component {\n  render() {\n    // Attach the passed-in className to the DOM node\n    return <div className={this.props.className} />\n  }\n}\n"
      })
    }), "\n", _jsx(_components.p, {
      children: "If you have pre-existing styles with a class, you can combine the global class with the\npassed-in one:"
    }), "\n", _jsx(_components.pre, {
      children: _jsx(_components.code, {
        className: "language-tsx",
        children: "class MyComponent extends React.Component {\n  render() {\n    // Attach the passed-in className to the DOM node\n    return <div className={`some-global-class ${this.props.className}`} />\n  }\n}\n"
      })
    }), "\n", _jsx(_components.h3, {
      children: "Issues with specificity"
    }), "\n", _jsx(_components.p, {
      children: "If you apply a global class together with a styled component class, the result might not be\nwhat you're expecting. If a property is defined in both classes with the same specificity,\nthe last one will win."
    }), "\n", _jsx(_components.pre, {
      children: _jsx(_components.code, {
        className: "language-tsx",
        children: "// MyComponent.js\nconst MyComponent = styled.div`background-color: green;`;\n\n// my-component.css\n.red-bg {\n  background-color: red;\n}\n\n// For some reason this component still has a green background,\n// even though you're trying to override it with the \"red-bg\" class!\n<MyComponent className=\"red-bg\" />\n"
      })
    }), "\n", _jsxs(_components.p, {
      children: ["In the above example the styled component class takes precedence over the global class, since\nstyled-components injects its styles during runtime at the end of the ", _jsx(_components.code, {
        children: "<head>"
      }), " by default.\nThus its styles win over other single classname selectors."]
    }), "\n", _jsx(_components.p, {
      children: "One solution is to bump up the specificity of the selectors in your stylesheet:"
    }), "\n", _jsx(_components.pre, {
      children: _jsx(_components.code, {
        className: "language-css",
        children: "/* my-component.css */\n.red-bg.red-bg {\n  background-color: red;\n}\n"
      })
    }), "\n", _jsx(_components.h3, {
      children: "Avoiding conflicts with third-party styles and scripts"
    }), "\n", _jsx(_components.p, {
      children: "If you deploy styled-components on a page you don't fully control, you may need to take\nprecautions to ensure that your component styles don't conflict with those of the host page."
    }), "\n", _jsx(_components.p, {
      children: "The most common problem is insufficient specificity. For example, consider a host page with this\nstyle rule:"
    }), "\n", _jsx(_components.pre, {
      children: _jsx(_components.code, {
        className: "language-css",
        children: "body.my-body button {\n  padding: 24px;\n}\n"
      })
    }), "\n", _jsx(_components.p, {
      children: "Since the rule contains a classname and two tag names, it has higher specificity than the single\nclassname selector generated by this styled component:"
    }), "\n", _jsx(_components.pre, {
      children: _jsx(_components.code, {
        className: "language-tsx",
        children: "styled.button`\n  padding: 16px;\n`\n"
      })
    }), "\n", _jsxs(_components.p, {
      children: ["There's no way to give your components complete immunity from the host page's styles, but you can\nat least boost the specificity of their style rules with\n", _jsx(_components.a, {
        href: "https://github.com/QuickBase/babel-plugin-styled-components-css-namespace",
        children: "babel-plugin-styled-components-css-namespace"
      }), ",\nwhich allows you to specify a CSS namespace for all of your styled components. A good namespace\nwould be something like ", _jsx(_components.code, {
        children: "#my-widget"
      }), ", if all of your styled-components render in a container\nwith ", _jsx(_components.code, {
        children: "id=\"my-widget\""
      }), ", since ID selectors have more specificity than any number of classnames."]
    }), "\n", _jsxs(_components.p, {
      children: ["A rarer problem is conflicts between two instances of styled-components on the page. You can avoid\nthis by defining ", _jsx(_components.code, {
        children: "process.env.SC_ATTR"
      }), " in the code bundle with your styled-components instance.\nThis value overrides the default ", _jsx(_components.code, {
        children: "<style>"
      }), " tag attribute, ", _jsx(_components.code, {
        children: "data-styled"
      }), " (", _jsx(_components.code, {
        children: "data-styled-components"
      }), " in v3 and lower), allowing\neach styled-components instance to recognize its own tags."]
    })]
  });
}
function MDXContent(props = {}) {
  const {wrapper: MDXLayout} = Object.assign({}, _provideComponents(), props.components);
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, props)
  })) : _createMdxContent(props);
}
export default MDXContent;
